export type MapScreenPoint = { x: number; y: number };
export type MapScreenSize = { x: number; y: number };

export type MapSafeInsets = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type MapCollisionOptions = {
  minDistance?: number;
  seed?: number;
  maxRadius?: number;
};

const clampNumber = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/**
 * Shared Big Blue safe viewport box.
 *
 * Header height is supplied by MapView so this module stays independent of DOM,
 * React, CSS variables, and route ownership.
 */
export const getBigBlueSafeInsets = (headerHeightPx: number): MapSafeInsets => ({
  left: 26,
  right: 26,
  top: Math.round(Math.max(0, Number(headerHeightPx) || 0) + 22),
  bottom: 185,
});

export const clampMapPointToSafeBox = (
  point: MapScreenPoint,
  size: MapScreenSize,
  insets: MapSafeInsets,
): MapScreenPoint => ({
  x: clampNumber(point.x, insets.left, size.x - insets.right),
  y: clampNumber(point.y, insets.top, size.y - insets.bottom),
});

export const isMapPointInsideSafeBox = (
  point: MapScreenPoint,
  size: MapScreenSize,
  insets: MapSafeInsets,
) =>
  point.x >= insets.left &&
  point.x <= size.x - insets.right &&
  point.y >= insets.top &&
  point.y <= size.y - insets.bottom;

/**
 * Resolve a presentation-only marker point around already occupied screen-space.
 * Canonical geographic coordinates remain owned by the caller.
 */
export const resolveScreenMarkerCollision = (
  desired: MapScreenPoint,
  occupied: MapScreenPoint[],
  size: MapScreenSize,
  insets: MapSafeInsets,
  opts?: MapCollisionOptions,
): MapScreenPoint => {
  const minDistance = Math.max(44, Number(opts?.minDistance || 66));
  const maxRadius = Math.max(minDistance, Number(opts?.maxRadius || 96));
  const seed = Math.abs(Number(opts?.seed || 0));
  const base = clampMapPointToSafeBox(desired, size, insets);

  const clearance = (point: MapScreenPoint) =>
    occupied.length
      ? Math.min(
          ...occupied.map((other) =>
            Math.hypot(point.x - other.x, point.y - other.y),
          ),
        )
      : Number.POSITIVE_INFINITY;

  if (clearance(base) >= minDistance) return base;

  let best = base;
  let bestClearance = clearance(base);
  const angleOffset = ((seed % 360) * Math.PI) / 180;
  const radii = [
    minDistance * 0.72,
    minDistance,
    minDistance * 1.28,
    maxRadius,
  ];

  for (const radius of radii) {
    for (let slot = 0; slot < 12; slot += 1) {
      const angle = angleOffset + (slot / 12) * Math.PI * 2;
      const candidate = clampMapPointToSafeBox(
        {
          x: desired.x + Math.cos(angle) * radius,
          y: desired.y + Math.sin(angle) * radius,
        },
        size,
        insets,
      );
      const score = clearance(candidate);
      if (score > bestClearance) {
        best = candidate;
        bestClearance = score;
      }
      if (score >= minDistance) return candidate;
    }
  }

  return best;
};
